import { useSupported } from "@vueuse/core";
import {
  computed,
  type MaybeRefOrGetter,
  shallowRef,
  watchEffect,
  type Ref,
  toValue,
} from "vue";

/**
 * Provide a bit more backwards compatibility than `@vueuse`
 * but don't patch the feature for SSR window size
 * @param query A Media Query string
 * @returns A reactive boolean that is true if the media query matches
 * @see https://vueuse.org/core/useMediaQuery
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/matchMedia
 */
export function useMediaQuery(
  query: MaybeRefOrGetter<string>,
  defaultWhenUnsupported: boolean = false
): Ref<boolean> {
  const isSupported = useSupported(
    () =>
      window &&
      "matchMedia" in window &&
      typeof window.matchMedia === "function"
  );

  const matches = shallowRef(false);
  const mediaQuery = shallowRef<MediaQueryList>();

  const handler = (event: MediaQueryListEvent) => {
    matches.value = event.matches;
  };

  watchEffect((onCleanup) => {
    if (!isSupported.value) return;

    const mediaQueryList = (mediaQuery.value = window.matchMedia(
      toValue(query)
    ));
    matches.value = mediaQueryList.matches;

    if (typeof mediaQueryList.addEventListener === "function") {
      mediaQueryList.addEventListener("change", handler, { passive: true });
    } else if (typeof mediaQueryList.addListener === "function") {
      mediaQueryList.addListener(handler);
    }

    onCleanup(() => {
      if (typeof mediaQueryList.removeEventListener === "function") {
        mediaQueryList.removeEventListener("change", handler);
      } else if (typeof mediaQueryList.removeListener === "function") {
        mediaQueryList.removeListener(handler);
      }
    });
  });

  return computed(() =>
    isSupported.value ? matches.value : defaultWhenUnsupported
  );
}
